import { isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

export interface LayoutConfig {
    preset: string;
    primary: string;
    surface: string | undefined | null;
    darkTheme: boolean;
    menuMode: string;
    menuTheme: string;
    cardStyle: string;
}

interface LayoutState {
    staticMenuInactive: boolean;
    overlayMenuActive: boolean;
    rightMenuVisible: boolean;
    configSidebarVisible: boolean;
    mobileMenuActive: boolean;
    searchBarActive: boolean;
    sidebarExpanded: boolean;
    menuHoverActive: boolean;
    activePath: string | null;
    anchored: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

type OptionalViewTransitionDocument = Document & {
    startViewTransition?: Document['startViewTransition'];
};

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    layoutConfig = signal<LayoutConfig>({
        preset: 'Lara',
        primary: 'blue',
        surface: null,
        darkTheme: false,
        menuMode: 'horizontal',
        menuTheme: 'light',
        cardStyle: 'transparent'
    });

    layoutState = signal<LayoutState>({
        staticMenuInactive: false,
        overlayMenuActive: false,
        rightMenuVisible: false,
        configSidebarVisible: false,
        mobileMenuActive: false,
        searchBarActive: false,
        sidebarExpanded: false,
        menuHoverActive: false,
        activePath: null,
        anchored: false
    });

    router = inject(Router);

    private readonly platformId = inject(PLATFORM_ID);

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    isSlim = computed(() => this.layoutConfig().menuMode === 'slim');

    isHorizontal = computed(() => this.layoutConfig().menuMode === 'horizontal');

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    isCompact = computed(() => this.layoutConfig().menuMode === 'compact');

    isStatic = computed(() => this.layoutConfig().menuMode === 'static');

    isReveal = computed(() => this.layoutConfig().menuMode === 'reveal');

    isDrawer = computed(() => this.layoutConfig().menuMode === 'drawer');

    hasOverlaySubmenu = computed(() => this.isSlim() || this.isCompact() || this.isHorizontal());

    hasOpenOverlay = computed(() => this.layoutState().overlayMenuActive || this.hasOpenOverlaySubmenu());

    hasOpenOverlaySubmenu = computed(() => {
        return this.hasOverlaySubmenu() && !!this.layoutState().activePath;
    });

    isSidebarStateChanged = computed(() => {
        const layoutConfig = this.layoutConfig();
        return layoutConfig.menuMode === 'horizontal' || layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus';
    });

    private initialized = false;

    private previousMenuMode: string | undefined = undefined;

    private viewTransitionInProgress = false;

    constructor() {
        this.loadCompanyConfig();

        effect(() => {
            const config = this.layoutConfig();
            if (!isPlatformBrowser(this.platformId)) {
                return;
            }

            // First run must sync `app-dark` from JWT/bootstrap; previously we returned early and never applied.
            if (!this.initialized) {
                this.initialized = true;
                this.toggleDarkMode(config);
                return;
            }

            this.handleDarkModeTransition(config);
        });

        effect(() => {
            this.updateMenuState();
        });
    }

    /**
     * JWT claims from the API use snake_case (see PROJECT_CONTEXT / JwtTokenService).
     * Accept camelCase too for older or alternate token shapes.
     */
    private static readJwtClaim(decoded: Record<string, unknown>, ...keys: string[]): string | undefined {
        for (const key of keys) {
            const v = decoded[key];
            if (typeof v === 'string' && v.trim()) {
                return v.trim();
            }
        }
        return undefined;
    }

    private loadCompanyConfig() {
        try {
            const token = localStorage.getItem('auth.access_token') || sessionStorage.getItem('auth.access_token');
            if (!token) {
                return;
            }

            const decoded = jwtDecode<Record<string, unknown>>(token);
            if (!decoded || typeof decoded !== 'object') {
                return;
            }

            const primary = LayoutService.readJwtClaim(
                decoded,
                'config_template_primary',
                'configTemplatePrimary'
            );
            const colorScheme = LayoutService.readJwtClaim(
                decoded,
                'config_template_color_scheme',
                'configTemplateColorScheme'
            );
            const menuTheme = LayoutService.readJwtClaim(
                decoded,
                'config_template_menu_theme',
                'configTemplateMenuTheme'
            );
            const menuType = LayoutService.readJwtClaim(decoded, 'config_template_menu_type', 'configTemplateMenuType');

            const hasAnyTemplateClaim = !!(primary || colorScheme || menuTheme || menuType);
            if (!hasAnyTemplateClaim) {
                return;
            }

            this.layoutConfig.update((config) => {
                const darkTheme =
                    colorScheme != null && colorScheme.length > 0 ? colorScheme.toLowerCase() === 'dark' : config.darkTheme;

                return {
                    preset: 'Lara',
                    primary: primary ?? config.primary,
                    surface: null,
                    darkTheme,
                    menuMode: menuType ?? config.menuMode,
                    menuTheme: menuTheme ?? config.menuTheme,
                    cardStyle: 'transparent'
                };
            });

            if (isPlatformBrowser(this.platformId)) {
                this.toggleDarkMode(this.layoutConfig());
            }
        } catch (error) {
            console.error('Failed to load company config from token:', error);
        }
    }

    private updateMenuState() {
        const menuMode = this.layoutConfig().menuMode;
        if (this.previousMenuMode === undefined) {
            this.previousMenuMode = menuMode;
            return;
        }

        if (this.previousMenuMode === menuMode) {
            return;
        }

        this.previousMenuMode = menuMode;

        const isOverlaySubmenu = menuMode === 'slim' || menuMode === 'slim-plus' || menuMode === 'horizontal' || menuMode === 'compact';

        this.layoutState.update((prev) => ({
            ...prev,
            staticMenuInactive: false,
            overlayMenuActive: false,
            mobileMenuActive: false,
            sidebarExpanded: false,
            menuHoverActive: false,
            anchored: false,
            activePath: this.isDesktop() ? (isOverlaySubmenu ? null : this.router.url) : prev.activePath
        }));
    }

    private handleDarkModeTransition(config: LayoutConfig): void {
        if (this.isDarkModeAlreadyApplied(config)) {
            return;
        }

        if (!this.canStartViewTransition()) {
            this.toggleDarkMode(config);
            return;
        }

        this.startViewTransition(config);
    }

    private startViewTransition(config: LayoutConfig): void {
        const viewTransitionDocument = document as OptionalViewTransitionDocument;

        if (this.viewTransitionInProgress) {
            this.toggleDarkMode(config);
            return;
        }

        try {
            this.viewTransitionInProgress = true;

            const transition = viewTransitionDocument.startViewTransition?.(() => {
                this.toggleDarkMode(config);
            });

            if (!transition) {
                this.viewTransitionInProgress = false;
                this.toggleDarkMode(config);
                return;
            }

            void transition.finished.catch(() => undefined).finally(() => {
                this.viewTransitionInProgress = false;
            });
        } catch {
            this.viewTransitionInProgress = false;
            this.toggleDarkMode(config);
        }
    }

    private canStartViewTransition(): boolean {
        const viewTransitionDocument = document as OptionalViewTransitionDocument;

        return (
            !this.viewTransitionInProgress &&
            typeof viewTransitionDocument.startViewTransition === 'function' &&
            viewTransitionDocument.visibilityState === 'visible' &&
            viewTransitionDocument.documentElement.isConnected
        );
    }

    private isDarkModeAlreadyApplied(config: LayoutConfig): boolean {
        return document.documentElement.classList.contains('app-dark') === config.darkTheme;
    }

    toggleDarkMode(config?: LayoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    toggleMenu() {
        if (this.isDesktop()) {
            if (this.layoutConfig().menuMode === 'static') {
                this.layoutState.update((prev) => ({ ...prev, staticMenuInactive: !prev.staticMenuInactive }));
            }

            if (this.layoutConfig().menuMode === 'overlay') {
                this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));
            }
        } else {
            this.layoutState.update((prev) => ({ ...prev, mobileMenuActive: !prev.mobileMenuActive }));
        }
    }

    changeMenuMode(mode: string) {
        this.layoutConfig.update((prev) => ({ ...prev, menuMode: mode }));
        this.layoutState.update((prev) => ({ ...prev, staticMenuInactive: false, overlayMenuActive: false, mobileMenuActive: false, sidebarExpanded: false, menuHoverActive: false, anchored: false }));

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, activePath: this.hasOverlaySubmenu() ? null : this.router.url }));
        }
    }

    toggleRightMenu() {
        this.layoutState.update((prev) => ({
            ...prev,
            rightMenuVisible: !prev.rightMenuVisible
        }));
    }

    toggleConfigSidebar() {
        this.layoutState.update((prev) => ({
            ...prev,
            configSidebarVisible: !prev.configSidebarVisible
        }));
    }

    toggleSearchBar() {
        this.layoutState.update((prev) => ({
            ...prev,
            searchBarActive: !prev.searchBarActive
        }));
    }

    isDesktop() {
        return window.innerWidth > 991;
    }
}
